import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/ui/use-toast';
import { supabaseFunctionWithRetry, supabaseQueryWithRetry } from '@/lib/networkRetry';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { WEARABLE_CONFIG } from '@/config/wearable';
import { createLogger } from '@/lib/structuredLogger';
import { invalidateWearableQueries } from '@/lib/wearableQueryInvalidation';
import { getLocalIsoDate, getUserTimezone } from '@/lib/dateUtils';

interface UseWearableSyncOptions {
  userId?: string;
  autoSync?: boolean;
}

interface WearableConnection {
  id: string;
  provider: string;
  status: string;
  last_synced_at: string | null;
  last_error: string | null;
  retry_count: number;
}

const logger = createLogger('useWearableSync');

export function useWearableSync({ userId, autoSync = true }: UseWearableSyncOptions) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lastResumeSyncRef = useRef<Date | null>(null);

  // Fetch wearable connections
  const { data: connections, isLoading: connectionsLoading } = useQuery<WearableConnection[]>({
    queryKey: QUERY_KEYS.WEARABLE_CONNECTIONS?.(userId ?? '') ?? ['wearable-connections', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabaseQueryWithRetry(() =>
        supabase
          .from('wearable_connections')
          .select('id, provider, status, last_synced_at, last_error, retry_count')
          .eq('user_id', userId)
          .in('status', ['active', 'connected']),
      );

      if (error) {
        logger.error('Error fetching wearable connections', error);
        throw error;
      }

      return (data ?? []) as WearableConnection[];
    },
    enabled: !!userId,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG,
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
  });

  // Check if any connection is stale (>24h since last sync)
  const hasStaleConnection = useCallback(() => {
    if (!connections?.length) return false;

    const now = Date.now();

    return connections.some((conn) => {
      if (!conn.last_synced_at) return true; // Never synced
      const lastSync = new Date(conn.last_synced_at).getTime();
      return now - lastSync > WEARABLE_CONFIG.STALE_THRESHOLD_MS;
    });
  }, [connections]);

  // Sync mutation for manual/catch-up sync
  const syncMutation = useMutation({
    mutationFn: async (options?: { provider?: string; days?: number }) => {
      if (!userId) throw new Error('User ID required');

      const provider = options?.provider ?? connections?.[0]?.provider;
      if (!provider) throw new Error('No wearable provider connected');

      const timezone = getUserTimezone();
      const today = getLocalIsoDate();
      const days = Math.max(1, Math.floor(options?.days ?? WEARABLE_CONFIG.HISTORICAL_SYNC_DAYS));
      const startDate = getLocalIsoDate(
        new Date(Date.now() - (days - 1) * WEARABLE_CONFIG.MS_PER_DAY),
      );

      logger.info('Starting wearable sync', { provider, startDate, endDate: today, timezone });

      const { data, error } = await supabaseFunctionWithRetry(supabase, 'wearable-sync', {
        body: {
          provider,
          start_date: startDate,
          end_date: today,
          timezone,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t('wearables.syncSuccess'),
        description: t('wearables.syncSuccessDescription'),
      });

      if (!userId) return;

      invalidateWearableQueries(queryClient, userId).catch((error) => {
        logger.warn('Failed to invalidate wearable queries after wearable sync', {
          userId,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    },
    onError: (error: Error) => {
      logger.error('Wearable sync failed', error);
      toast({
        title: t('wearables.syncFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // On-login catch-up sync: triggers when app becomes visible and connection is stale
  useEffect(() => {
    if (!autoSync || !userId || !connections?.length) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const now = new Date();
      const lastSync = lastResumeSyncRef.current;

      // Only sync if:
      // 1. Has stale connections (>24h)
      // 2. Haven't synced in the last 4 hours (via this hook)
      const shouldSync =
        hasStaleConnection() &&
        (!lastSync || now.getTime() - lastSync.getTime() > WEARABLE_CONFIG.RESUME_SYNC_COOLDOWN_MS);

      if (shouldSync) {
        logger.info('App resumed with stale wearable connections, triggering catch-up sync');

        try {
          // Sync each stale connection
          for (const conn of connections) {
            if (!conn.last_synced_at) {
              // Never synced - do 7 day catch-up
              await syncMutation.mutateAsync({
                provider: conn.provider,
                days: WEARABLE_CONFIG.HISTORICAL_SYNC_DAYS,
              });
            } else {
              const lastSyncTime = new Date(conn.last_synced_at).getTime();
              const staleness = now.getTime() - lastSyncTime;

              if (staleness > WEARABLE_CONFIG.STALE_THRESHOLD_MS) {
                // Calculate days to sync based on staleness
                const daysSinceSync = Math.min(
                  Math.ceil(staleness / WEARABLE_CONFIG.MS_PER_DAY),
                  WEARABLE_CONFIG.HISTORICAL_SYNC_DAYS,
                );
                await syncMutation.mutateAsync({ provider: conn.provider, days: daysSinceSync });
              }
            }
          }

          lastResumeSyncRef.current = now;
          logger.info('Wearable catch-up sync completed');
        } catch (error) {
          logger.error(
            'Wearable catch-up sync failed',
            error instanceof Error ? error : new Error(String(error)),
          );
          // Don't show toast for background operations
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    // Also check on mount if stale
    if (hasStaleConnection()) {
      handleVisibilityChange();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [autoSync, userId, connections, hasStaleConnection, syncMutation]);

  return {
    connections,
    connectionsLoading,
    hasStaleConnection: hasStaleConnection(),
    syncMutation,
    isSyncing: syncMutation.isPending,
  };
}
